import { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { useNavigate, useLocation } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'
import { syncSessionToCookie } from '@/lib/crossDomainAuth'
import { isCurrentAuthSessionValid, registerCurrentAuthSession } from '@/lib/authSessions'

// Hook para rastrear re-renders do AuthProvider
let authRenderCount = 0

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  const initializedRef = useRef(false)
  const initialSessionSetRef = useRef(false)
  
  
  // Atualizar ref quando navigate mudar
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  const AUTH_SESSION_TIMEOUT_MS = 15000; // 15s - evita loading infinito se a rede travar

  const rejectInvalidServerSession = useCallback(async () => {
    console.warn('[Auth] Sessão inválida ou expirada no servidor — solicitando novo login.')
    await supabase.auth.signOut({ scope: 'local' })
    syncSessionToCookie(null)
    setSession(null)
    setUser(null)
    setLoading(false)
    initializedRef.current = true
    initialSessionSetRef.current = false
    navigateRef.current('/login', { replace: true })
  }, [])

  useEffect(() => {
    // Get initial session e verificar confirmação
    const initSession = async () => {
      try {
        const getSessionWithTimeout = () =>
          Promise.race([
            supabase.auth.getSession(),
            new Promise<{ data: { session: null }; error: Error }>((_, reject) =>
              setTimeout(() => reject(new Error('Timeout ao obter sessão (rede lenta ou indisponível)')), AUTH_SESSION_TIMEOUT_MS)
            ),
          ]);
        const { data: { session: initialSession }, error } = await getSessionWithTimeout();
        
        if (error) {
          // Se houver erro de autenticação (como refresh token inválido), limpar sessão
          if (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found')) {
            console.warn('Refresh token inválido, limpando sessão...');
            await supabase.auth.signOut({ scope: 'local' });
            setSession(null);
            setUser(null);
            setLoading(false);
            initializedRef.current = true;
            return;
          }
          throw error;
        }
        
        // Verificar se é um usuário OAuth (Google, etc) - emails OAuth são automaticamente confirmados
        const isOAuthUser = initialSession?.user?.app_metadata?.provider && 
                           initialSession.user.app_metadata.provider !== 'email';
        
        if (initialSession?.user && !initialSession.user.email_confirmed_at && !isOAuthUser) {
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
        } else if (initialSession?.user) {
          const sessionValid = await isCurrentAuthSessionValid()
          if (sessionValid === false) {
            await rejectInvalidServerSession()
            return
          }

          setSession(initialSession);
          setUser(initialSession.user);
          syncSessionToCookie(initialSession);
          initialSessionSetRef.current = true;
          mixpanel.identify(initialSession.user.id)
          mixpanel.people.set({
            $email: initialSession.user.email,
            $name: initialSession.user.user_metadata?.full_name || initialSession.user.email,
          })
          void registerCurrentAuthSession()
        } else {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
          syncSessionToCookie(initialSession);
        }
      } catch (error: any) {
        console.error('Erro ao buscar sessão inicial:', error);
        if (error?.message?.includes('Timeout')) {
          console.warn('Auth: sessão não respondeu a tempo. Verifique a conexão ou abra a aba Network (F12) antes de recarregar.');
        }
        // Em caso de erro, limpar sessão e redirecionar para login
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
        initializedRef.current = true;
      }
    };

    initSession();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        try {
          // Guard robusto: ignorar INITIAL_SESSION se já inicializamos completamente
          // Isso evita que o Supabase dispare INITIAL_SESSION novamente ao retomar foco
          if (event === 'INITIAL_SESSION' && initializedRef.current && initialSessionSetRef.current) {
            // Log removido para limpar console
            return;
          }

          // Marcar como inicializado na primeira vez que receber INITIAL_SESSION
          if (event === 'INITIAL_SESSION' && !initializedRef.current) {
            initializedRef.current = true;
          }

          if (event === 'SIGNED_OUT') {
            mixpanel.reset()
            syncSessionToCookie(null);
            setSession(null);
            setUser(null);
            setLoading(false);
            initializedRef.current = false;
            initialSessionSetRef.current = false;
            navigateRef.current('/login', { replace: true });
            return;
          }

          // Verificar se é um usuário OAuth (Google, etc) - emails OAuth são automaticamente confirmados
          const isOAuthUser = currentSession?.user?.app_metadata?.provider && 
                             currentSession.user.app_metadata.provider !== 'email';
          
          if (currentSession?.user && !currentSession.user.email_confirmed_at && !isOAuthUser) {
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setLoading(false);
            return;
          }

          if (currentSession?.user) {
            const shouldValidateSession =
              event === 'SIGNED_IN' ||
              (event === 'INITIAL_SESSION' && !initialSessionSetRef.current)

            if (shouldValidateSession) {
              const sessionValid = await isCurrentAuthSessionValid()
              if (sessionValid === false) {
                await rejectInvalidServerSession()
                return
              }
            }
          }

          // Só atualizar se não for INITIAL_SESSION ou se ainda não inicializamos
          if (event !== 'INITIAL_SESSION' || !initializedRef.current) {
            setSession(currentSession);
            setUser(currentSession?.user ?? null);
            syncSessionToCookie(currentSession);
            setLoading(false);
          }

          if (
            currentSession?.user &&
            (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION')
          ) {
            void registerCurrentAuthSession();
          }
        } catch (error: any) {
          console.error('Erro no listener de auth state:', error);
          // Em caso de erro de autenticação, limpar sessão
          if (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found')) {
            setSession(null);
            setUser(null);
            setLoading(false);
            navigateRef.current('/login', { replace: true });
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [rejectInvalidServerSession]);

  const signOut = useCallback(async () => {
    try {
      // AUTH-06: logout apenas neste dispositivo; outras sessões permanecem ativas.
      await supabase.auth.signOut({ scope: 'local' })
    } catch (error: any) {
      console.error('Error signing out:', error.message || error)
    } finally {
      syncSessionToCookie(null)
      // Garantir limpeza local mesmo se o Supabase falhar
      setSession(null)
      setUser(null)
      window.location.replace('https://www.fluity.com.br/chat')
    }
  }, [])

  // Memoizar baseado apenas nos IDs para evitar re-renders quando o objeto é recriado
  const userId = user?.id ?? null
  const sessionId = session?.access_token ?? null
  const value = useMemo(() => ({ user, session, loading, signOut }), [userId, sessionId, loading, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Protected Route Component
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!loading && !user) {
      // Verificar se a rota atual é pública
      const currentPath = location.pathname;
      const isPublic = [
        '/',
        '/login',
        '/signup',
        '/verify-email',
        '/auth/callback',
        '/accept-invite',
        '/pending-access',
      ].includes(currentPath);

      if (!isPublic) {
        // Preservar a página atual para redirecionar após o login (ex.: F5 ou sessão expirada)
        const returnTo = encodeURIComponent(location.pathname + location.search + (location.hash || ''));
        navigate(`/login?redirect=${returnTo}`, { replace: true });
      }
    }
  }, [user, loading, navigate, location.pathname, location.search, location.hash])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return <>{children}</>
}
